import {Time} from 'yougile-util'
import {DataObjectId, extractStickerState, TaskData} from 'yougile-model'

const day = 24 * 3600_000

interface TimelinePos {
  depth: number
  startDay: number
  stopDay: number
}

export class TimelinePosition {
  public static getPosition(task: TaskData): TimelinePos {
    if (!task) {
      return undefined
    }

    const startDay = Math.round(
      Time.middleOfDay(
        extractStickerState(task.startDate) ||
          extractStickerState(task.deadline)
      ) / day
    )

    const stopDay = Math.round(
      Time.middleOfDay(extractStickerState(task.deadline)) / day
    )

    const newStartDay = startDay > stopDay ? stopDay : startDay
    const newStopDay = startDay > stopDay ? startDay : stopDay

    return {
      depth: task.timelineDepth || 0,
      startDay: newStartDay,
      stopDay: newStopDay
    }
  }

  public static getPositions(
    tasks: TaskData[],
    forcedId: DataObjectId
  ): Map<DataObjectId, TimelinePos> {
    if (!tasks) {
      return new Map()
    }
    const filledDays: Map<number, Set<number>> = new Map()
    const positions: Map<DataObjectId, TimelinePos> = new Map()
    tasks.forEach(t => positions.set(t.id, TimelinePosition.getPosition(t)))
    const ord = (t: TaskData) =>
      forcedId && t.id === forcedId ? -1 : positions.get(t.id).stopDay
    const sorted = [...tasks].sort((a, b) => ord(a) - ord(b))

    const place = (t: TaskData) => {
      const pos = positions.get(t.id)
      const filled: Set<number> = new Set()
      for (let d = pos.startDay; d <= pos.stopDay; d++) {
        if (filledDays.has(d)) {
          filledDays.get(d).forEach(n => filled.add(n))
        }
      }
      let attemptedDepth = pos.depth || 0
      while (filled.has(attemptedDepth)) {
        attemptedDepth++
      }
      positions.set(t.id, {...pos, depth: attemptedDepth})

      for (let d = pos.startDay; d <= pos.stopDay; d++) {
        if (!filledDays.has(d)) {
          filledDays.set(d, new Set())
        }
        filledDays.get(d).add(attemptedDepth)
      }
    }
    sorted.forEach(t => place(t))
    return positions
  }

  public static removeEmptyLines(
    positions: Map<DataObjectId, TimelinePos>,
    fromLine = 0
  ): Map<DataObjectId, TimelinePos> {
    const lines: Set<number> = new Set()
    const res: Map<DataObjectId, TimelinePos> = new Map()
    positions.forEach(p => {
      lines.add(p.depth)
    })
    const transform: Map<number, number> = new Map()
    const max = Math.max(...lines)
    let empty = 0
    for (let n = 0; n <= max; n++) {
      if (!lines.has(n)) {
        if (n >= fromLine) {
          empty++
        }
      } else {
        transform.set(n, n - empty)
      }
    }
    positions.forEach((p, id) => {
      res.set(id, {...p, depth: transform.get(p.depth)})
    })
    return res
  }
}
